<template>
  <main class="medhop-status-page">
    <span class="status-orb status-orb--top" aria-hidden="true"></span>
    <span class="status-orb status-orb--bottom" aria-hidden="true"></span>

    <header class="status-header">
      <router-link to="/login" class="status-brand" aria-label="Kembali ke Medhop">
        <img src="/images/logo-medhop-transparent.png" alt="Medhop" />
      </router-link>
      <span class="status-system">
        <span class="status-system__dot" aria-hidden="true"></span>
        Medhop Clinic Platform
      </span>
    </header>

    <section class="status-content" aria-labelledby="status-title">
      <div class="status-visual" aria-hidden="true">
        <span class="status-visual__ring"></span>
        <span class="status-visual__ring status-visual__ring--small"></span>
        <div class="status-code">{{ statusData.code }}</div>
      </div>

      <div class="status-copy">
        <span class="status-eyebrow">{{ statusData.eyebrow }}</span>
        <h1 id="status-title">{{ statusData.title }}</h1>
        <p>{{ statusData.description }}</p>

        <div class="status-actions">
          <button class="status-button status-button--primary" type="button" @click="primaryAction">
            <i :class="statusData.retry ? 'fas fa-redo-alt' : 'fas fa-home'" aria-hidden="true"></i>
            {{ statusData.retry ? "Coba lagi" : "Kembali ke halaman utama" }}
          </button>
          <button class="status-button status-button--secondary" type="button" @click="goBack">
            <i class="fas fa-arrow-left" aria-hidden="true"></i>
            Kembali
          </button>
        </div>

        <div class="status-help">
          <i class="fas fa-headset" aria-hidden="true"></i>
          <span>
            Masalah masih berlanjut? Hubungi tim Medhop dan sertakan kode
            <strong>{{ statusData.code }}</strong>.
          </span>
        </div>
      </div>
    </section>

    <footer class="status-footer">
      <span>&copy; {{ year }} Medhop Software</span>
      <span>Platform kesehatan digital Indonesia</span>
    </footer>
  </main>
</template>

<script>
const STATUS_CONTENT = {
  400: {
    code: "400",
    eyebrow: "Permintaan tidak valid",
    title: "Data yang dikirim belum tepat",
    description:
      "Sistem tidak dapat memproses permintaan ini. Periksa kembali data yang Anda masukkan lalu coba lagi.",
    retry: false,
  },
  401: {
    code: "401",
    eyebrow: "Sesi diperlukan",
    title: "Silakan masuk kembali",
    description:
      "Sesi Anda sudah berakhir atau belum terautentikasi. Masuk kembali untuk melanjutkan aktivitas.",
    retry: false,
  },
  403: {
    code: "403",
    eyebrow: "Akses terbatas",
    title: "Anda tidak memiliki izin",
    description:
      "Akun ini belum memiliki hak akses ke halaman tersebut. Hubungi administrator klinik bila diperlukan.",
    retry: false,
  },
  404: {
    code: "404",
    eyebrow: "Halaman tidak ditemukan",
    title: "Sepertinya Anda tersesat",
    description:
      "Alamat yang dituju mungkin salah, sudah dipindahkan, atau tidak lagi tersedia di Medhop.",
    retry: false,
  },
  408: {
    code: "408",
    eyebrow: "Waktu permintaan habis",
    title: "Proses membutuhkan waktu terlalu lama",
    description:
      "Koneksi terputus sebelum proses selesai. Pastikan jaringan stabil, kemudian coba kembali.",
    retry: true,
  },
  429: {
    code: "429",
    eyebrow: "Terlalu banyak permintaan",
    title: "Tunggu sebentar, lalu coba lagi",
    description:
      "Sistem menerima terlalu banyak permintaan dalam waktu singkat untuk menjaga keamanan layanan.",
    retry: true,
  },
  500: {
    code: "500",
    eyebrow: "Gangguan pada sistem",
    title: "Ada kendala di sisi kami",
    description:
      "Medhop mengalami gangguan saat memproses permintaan. Data Anda tetap aman dan tim kami dapat menanganinya.",
    retry: true,
  },
  502: {
    code: "502",
    eyebrow: "Layanan penghubung bermasalah",
    title: "Respons layanan belum tersedia",
    description:
      "Salah satu layanan yang terhubung ke Medhop memberikan respons yang tidak valid. Silakan coba kembali.",
    retry: true,
  },
  503: {
    code: "503",
    eyebrow: "Layanan sementara tidak tersedia",
    title: "Kami sedang melakukan pemeliharaan",
    description:
      "Medhop sedang mempersiapkan layanan agar kembali optimal. Silakan coba kembali beberapa saat lagi.",
    retry: true,
  },
  504: {
    code: "504",
    eyebrow: "Gateway timeout",
    title: "Layanan terhubung belum merespons",
    description:
      "Waktu tunggu ke layanan pendukung telah habis. Periksa koneksi dan coba kembali dalam beberapa saat.",
    retry: true,
  },
};

export default {
  name: "MedhopStatusPage",
  props: {
    status: {
      type: Number,
      default: 404,
    },
  },
  computed: {
    statusData() {
      return STATUS_CONTENT[this.status] || STATUS_CONTENT[404];
    },
    year() {
      return new Date().getFullYear();
    },
  },
  methods: {
    primaryAction() {
      if (this.statusData.retry) {
        window.location.reload();
        return;
      }

      this.$router.push("/login").catch(() => {});
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
        return;
      }

      this.$router.push("/login").catch(() => {});
    },
  },
};
</script>

<style scoped>
.medhop-status-page {
  --status-navy: #143263;
  --status-blue: #14608f;
  --status-teal: #1e958f;
  position: relative;
  display: grid;
  min-height: 100vh;
  padding: 28px clamp(24px, 6vw, 88px);
  overflow: hidden;
  grid-template-rows: auto 1fr auto;
  color: #172235;
  background:
    radial-gradient(circle at 7% 8%, rgba(30, 149, 143, 0.13), transparent 25%),
    radial-gradient(circle at 94% 88%, rgba(20, 96, 143, 0.13), transparent 28%),
    #f3f7fb;
  font-family: "Poppins", "Inter", sans-serif;
}

.medhop-status-page ::selection {
  color: #143263;
  background: #a9e5e1;
  text-shadow: none;
}

.status-orb {
  position: absolute;
  width: 260px;
  height: 260px;
  border: 38px solid rgba(20, 96, 143, 0.07);
  border-radius: 50%;
  pointer-events: none;
}

.status-orb--top {
  top: -174px;
  right: 8%;
}

.status-orb--bottom {
  bottom: -184px;
  left: 4%;
  border-color: rgba(30, 149, 143, 0.08);
}

.status-header,
.status-footer,
.status-content {
  position: relative;
  z-index: 1;
}

.status-header,
.status-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.status-brand {
  display: inline-flex;
  align-items: center;
}

.status-brand img {
  width: 150px;
  height: 44px;
  object-fit: contain;
  object-position: left center;
}

.status-system {
  display: inline-flex;
  padding: 9px 14px;
  align-items: center;
  gap: 9px;
  border: 1px solid rgba(20, 50, 99, 0.08);
  border-radius: 999px;
  color: #536277;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 10px 30px rgba(20, 50, 99, 0.05);
  font-size: 12px;
  font-weight: 600;
  backdrop-filter: blur(12px);
}

.status-system__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--status-teal);
  box-shadow: 0 0 0 5px rgba(30, 149, 143, 0.12);
}

.status-content {
  display: grid;
  width: min(1080px, 100%);
  margin: auto;
  padding: 56px 0;
  align-items: center;
  grid-template-columns: minmax(300px, 0.9fr) minmax(360px, 1.1fr);
  gap: clamp(40px, 8vw, 104px);
}

.status-visual {
  position: relative;
  display: grid;
  min-height: 340px;
  place-items: center;
}

.status-visual::before {
  position: absolute;
  width: 280px;
  height: 280px;
  content: "";
  border-radius: 42% 58% 57% 43% / 43% 42% 58% 57%;
  background: linear-gradient(145deg, var(--status-navy), var(--status-blue) 55%, var(--status-teal));
  box-shadow: 0 36px 70px rgba(20, 50, 99, 0.2);
  transform: rotate(-8deg);
}

.status-visual__ring {
  position: absolute;
  width: 330px;
  height: 330px;
  border: 1px solid rgba(20, 96, 143, 0.16);
  border-radius: 50%;
}

.status-visual__ring--small {
  width: 220px;
  height: 220px;
  border-color: rgba(255, 255, 255, 0.2);
  z-index: 1;
}

.status-code {
  position: relative;
  z-index: 2;
  color: #fff;
  font-size: clamp(72px, 9vw, 118px);
  font-weight: 800;
  letter-spacing: -0.08em;
  line-height: 1;
  text-shadow: 0 10px 30px rgba(7, 29, 62, 0.24);
}

.status-eyebrow {
  display: inline-flex;
  margin-bottom: 16px;
  color: var(--status-teal);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.status-copy h1 {
  max-width: 560px;
  margin: 0 0 18px;
  color: #172235 !important;
  font-size: clamp(34px, 4vw, 54px);
  font-weight: 700;
  letter-spacing: -0.045em;
  line-height: 1.12;
}

.status-copy > p {
  max-width: 590px;
  margin: 0;
  color: #5e6c80 !important;
  font-size: 16px;
  line-height: 1.8;
}

.status-actions {
  display: flex;
  margin-top: 34px;
  flex-wrap: wrap;
  gap: 12px;
}

.status-button {
  display: inline-flex;
  min-height: 48px;
  padding: 0 21px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 0;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  transition: transform 160ms ease, box-shadow 160ms ease, background 160ms ease;
}

.status-button:hover {
  transform: translateY(-2px);
}

.status-button:focus-visible {
  outline: 3px solid rgba(30, 149, 143, 0.22);
  outline-offset: 3px;
}

.status-button--primary {
  color: #fff;
  background: linear-gradient(135deg, var(--status-navy), var(--status-blue), var(--status-teal));
  box-shadow: 0 16px 30px rgba(20, 96, 143, 0.2);
}

.status-button--primary:hover {
  color: #fff;
  box-shadow: 0 20px 36px rgba(20, 96, 143, 0.28);
}

.status-button--secondary {
  color: var(--status-navy);
  border: 1px solid #dce6ef;
  background: rgba(255, 255, 255, 0.82);
}

.status-button--secondary:hover {
  color: var(--status-blue);
  border-color: rgba(20, 96, 143, 0.25);
  background: #fff;
}

.status-help {
  display: flex;
  max-width: 590px;
  margin-top: 34px;
  padding-top: 24px;
  align-items: flex-start;
  gap: 12px;
  border-top: 1px solid #dfe8f0;
  color: #778397;
  font-size: 12px;
  line-height: 1.65;
}

.status-help i {
  margin-top: 3px;
  color: var(--status-teal);
}

.status-help strong {
  color: var(--status-navy);
}

.status-footer {
  color: #8692a4;
  font-size: 11px;
}

@media (max-width: 860px) {
  .medhop-status-page {
    padding: 22px;
  }

  .status-content {
    padding: 38px 0;
    grid-template-columns: 1fr;
    gap: 28px;
    text-align: center;
  }

  .status-visual {
    min-height: 260px;
  }

  .status-visual::before {
    width: 220px;
    height: 220px;
  }

  .status-visual__ring {
    width: 260px;
    height: 260px;
  }

  .status-visual__ring--small {
    width: 170px;
    height: 170px;
  }

  .status-copy h1,
  .status-copy > p,
  .status-help {
    margin-right: auto;
    margin-left: auto;
  }

  .status-actions {
    justify-content: center;
  }

  .status-help {
    justify-content: center;
    text-align: left;
  }
}

@media (max-width: 540px) {
  .status-system {
    display: none;
  }

  .status-brand img {
    width: 132px;
  }

  .status-content {
    padding-top: 24px;
  }

  .status-visual {
    min-height: 220px;
  }

  .status-visual::before {
    width: 184px;
    height: 184px;
  }

  .status-visual__ring {
    width: 216px;
    height: 216px;
  }

  .status-visual__ring--small {
    width: 142px;
    height: 142px;
  }

  .status-copy h1 {
    font-size: 32px;
  }

  .status-copy > p {
    font-size: 14px;
  }

  .status-actions {
    flex-direction: column;
  }

  .status-button {
    width: 100%;
  }

  .status-footer {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }
}
</style>
